import type { StudioScenarioPayload, StudioScenarioPreset } from "./scenarioPresets";
import type {
  StudioTextPreviewChannel,
  StudioTextPreviewResult,
  StudioVoicePreviewExecutionResult,
  StudioVoicePreviewHumanizationSummary,
  StudioVoicePreviewLane,
} from "./studioPreviewCenter";

export const STUDIO_PREVIEW_EVIDENCE_LEDGER_VERSION = "a22.7_v1" as const;
export const STUDIO_PREVIEW_EVIDENCE_LEDGER_STORAGE_KEY =
  "vetcan.studio.preview.evidence";
const STUDIO_PREVIEW_EVIDENCE_MAX_ENTRIES = 24;
const STUDIO_PREVIEW_EVIDENCE_EXCERPT_LIMIT = 220;

export type StudioPreviewEvidenceType = "scenario_run" | "text_preview";
export type StudioPreviewEvidenceStatus =
  | "rendered"
  | "review"
  | "blocked"
  | "provider_error"
  | "matched"
  | "no_match";

export type StudioPreviewEvidenceEntry = {
  id: string;
  type: StudioPreviewEvidenceType;
  recordedAt: string;
  sourceLabel: string;
  status: StudioPreviewEvidenceStatus;
  channel: string;
  personaLabel: string | null;
  personaKey: string | null;
  requestId: string | null;
  correlationId: string | null;
  scenarioId: string | null;
  scenarioLabel: string | null;
  responseKey: string | null;
  intent: string | null;
  transcriptExcerpt: string | null;
  assistantExcerpt: string | null;
  humanization: StudioVoicePreviewHumanizationSummary | null;
};

type StudioPreviewEvidenceLedgerRecord = {
  version: typeof STUDIO_PREVIEW_EVIDENCE_LEDGER_VERSION;
  entries: StudioPreviewEvidenceEntry[];
};

const SECRET_REPLACEMENTS: Array<[RegExp, string]> = [
  [/\b(Bearer)\s+[A-Za-z0-9._-]+\b/gi, "$1 [redacted]"],
  [/\b(ELEVENLABS_API_KEY\s*[:=]\s*)([^\s,;]+)/gi, "$1[redacted]"],
  [/\b(API[_ -]?KEY\s*[:=]\s*)([^\s,;]+)/gi, "$1[redacted]"],
  [/\b(TOKEN\s*[:=]\s*)([^\s,;]+)/gi, "$1[redacted]"],
  [/\b(SECRET\s*[:=]\s*)([^\s,;]+)/gi, "$1[redacted]"],
];

function isRecord(value: unknown): value is Record<string, unknown> {
  return Boolean(value) && typeof value === "object" && !Array.isArray(value);
}

function localStorageAvailable() {
  return typeof window !== "undefined" && typeof window.localStorage !== "undefined";
}

function sanitizeStudioEvidenceText(value: string | null | undefined) {
  const sanitized = SECRET_REPLACEMENTS.reduce((current, [pattern, replacement]) => {
    return current.replace(pattern, replacement);
  }, String(value || ""));

  return sanitized.trim().slice(0, STUDIO_PREVIEW_EVIDENCE_EXCERPT_LIMIT) || null;
}

function sanitizeHumanization(
  humanization: StudioVoicePreviewHumanizationSummary | null | undefined
) {
  if (!humanization) {
    return null;
  }

  return {
    ...humanization,
  };
}

function sanitizeEvidenceEntry(input: unknown): StudioPreviewEvidenceEntry | null {
  if (!isRecord(input)) {
    return null;
  }

  const type =
    input.type === "scenario_run" || input.type === "text_preview"
      ? input.type
      : null;
  const statusOptions = [
    "rendered",
    "review",
    "blocked",
    "provider_error",
    "matched",
    "no_match",
  ] as const;
  const status = statusOptions.includes(input.status as StudioPreviewEvidenceStatus)
    ? (input.status as StudioPreviewEvidenceStatus)
    : null;

  if (!type || !status || typeof input.recordedAt !== "string") {
    return null;
  }

  return {
    id:
      typeof input.id === "string" && input.id.trim()
        ? input.id
        : `studio_preview_evidence_${input.recordedAt}`,
    type,
    recordedAt: input.recordedAt,
    sourceLabel:
      typeof input.sourceLabel === "string" && input.sourceLabel.trim()
        ? input.sourceLabel
        : "Studio preview evidence",
    status,
    channel: typeof input.channel === "string" ? input.channel : "unknown",
    personaLabel: sanitizeStudioEvidenceText(
      typeof input.personaLabel === "string" ? input.personaLabel : null
    ),
    personaKey: sanitizeStudioEvidenceText(
      typeof input.personaKey === "string" ? input.personaKey : null
    ),
    requestId: sanitizeStudioEvidenceText(
      typeof input.requestId === "string" ? input.requestId : null
    ),
    correlationId: sanitizeStudioEvidenceText(
      typeof input.correlationId === "string" ? input.correlationId : null
    ),
    scenarioId: sanitizeStudioEvidenceText(
      typeof input.scenarioId === "string" ? input.scenarioId : null
    ),
    scenarioLabel: sanitizeStudioEvidenceText(
      typeof input.scenarioLabel === "string" ? input.scenarioLabel : null
    ),
    responseKey: sanitizeStudioEvidenceText(
      typeof input.responseKey === "string" ? input.responseKey : null
    ),
    intent: sanitizeStudioEvidenceText(
      typeof input.intent === "string" ? input.intent : null
    ),
    transcriptExcerpt: sanitizeStudioEvidenceText(
      typeof input.transcriptExcerpt === "string" ? input.transcriptExcerpt : null
    ),
    assistantExcerpt: sanitizeStudioEvidenceText(
      typeof input.assistantExcerpt === "string" ? input.assistantExcerpt : null
    ),
    humanization: sanitizeHumanization(
      isRecord(input.humanization)
        ? (input.humanization as StudioVoicePreviewHumanizationSummary)
        : null
    ),
  };
}

function parseLedger(raw: string): StudioPreviewEvidenceLedgerRecord | null {
  try {
    const parsed = JSON.parse(raw) as unknown;
    if (!isRecord(parsed)) {
      return null;
    }

    if (parsed.version !== STUDIO_PREVIEW_EVIDENCE_LEDGER_VERSION) {
      return null;
    }

    if (!Array.isArray(parsed.entries)) {
      return null;
    }

    return {
      version: STUDIO_PREVIEW_EVIDENCE_LEDGER_VERSION,
      entries: parsed.entries
        .map((entry) => sanitizeEvidenceEntry(entry))
        .filter((entry): entry is StudioPreviewEvidenceEntry => Boolean(entry)),
    };
  } catch {
    return null;
  }
}

function serializeLedger(entries: StudioPreviewEvidenceEntry[]) {
  return JSON.stringify({
    version: STUDIO_PREVIEW_EVIDENCE_LEDGER_VERSION,
    entries,
  } satisfies StudioPreviewEvidenceLedgerRecord);
}

function safeStoredTranscript(text: string | null | undefined) {
  return sanitizeStudioEvidenceText(text);
}

function scenarioStatus(
  preview: NonNullable<StudioVoicePreviewExecutionResult["preview"]>
): StudioPreviewEvidenceStatus {
  if (preview.status === "blocked") {
    return "blocked";
  }

  if (preview.status === "provider_error") {
    return "provider_error";
  }

  return preview.humanization.total.status === "pass" ? "rendered" : "review";
}

export function createStudioScenarioEvidenceEntry(params: {
  preset: StudioScenarioPreset;
  payload: StudioScenarioPayload;
  preview: NonNullable<StudioVoicePreviewExecutionResult["preview"]>;
  voiceLane: StudioVoicePreviewLane | null;
  recordedAt?: string;
}) {
  const recordedAt = params.recordedAt || new Date().toISOString();

  return sanitizeEvidenceEntry({
    id: `scenario_${params.preset.id}_${recordedAt}`,
    type: "scenario_run",
    recordedAt,
    sourceLabel: `Scenario run / ${params.preset.label}`,
    status: scenarioStatus(params.preview),
    channel: params.preview.channel || params.payload.channel,
    personaLabel:
      params.voiceLane?.personaSelection.personaName || params.preview.personaKey || null,
    personaKey: params.preview.personaKey || params.voiceLane?.personaSelection.personaKey,
    requestId: params.preview.requestId || null,
    correlationId: params.preview.correlationId || params.payload.correlationId,
    scenarioId: params.preset.id,
    scenarioLabel: params.preset.label,
    responseKey: null,
    intent: null,
    transcriptExcerpt: safeStoredTranscript(params.payload.text),
    assistantExcerpt: safeStoredTranscript(params.payload.transcriptLines.join(" ")),
    humanization: params.preview.humanization,
  });
}

export function createStudioTextPreviewEvidenceEntry(params: {
  channel: StudioTextPreviewChannel;
  result: StudioTextPreviewResult;
  voiceLane: StudioVoicePreviewLane | null;
  recordedAt?: string;
}) {
  const recordedAt = params.recordedAt || new Date().toISOString();

  return sanitizeEvidenceEntry({
    id: `text_preview_${recordedAt}`,
    type: "text_preview",
    recordedAt,
    sourceLabel: "Text preview",
    status: params.result.matched ? "matched" : "no_match",
    channel: params.channel,
    personaLabel: params.voiceLane?.personaSelection.personaName || null,
    personaKey: params.voiceLane?.personaSelection.personaKey || null,
    requestId: null,
    correlationId: null,
    scenarioId: null,
    scenarioLabel: null,
    responseKey: params.result.key || null,
    intent: params.result.intent || null,
    transcriptExcerpt: params.result.key || params.result.intent || "text preview request",
    assistantExcerpt: params.result.reply || null,
    humanization: null,
  });
}

export function loadStudioPreviewEvidenceLedger() {
  const defaults: StudioPreviewEvidenceLedgerRecord = {
    version: STUDIO_PREVIEW_EVIDENCE_LEDGER_VERSION,
    entries: [],
  };

  if (!localStorageAvailable()) {
    return defaults;
  }

  try {
    const raw = localStorage.getItem(STUDIO_PREVIEW_EVIDENCE_LEDGER_STORAGE_KEY);
    if (!raw) {
      return defaults;
    }

    const parsed = parseLedger(raw);
    if (!parsed) {
      localStorage.removeItem(STUDIO_PREVIEW_EVIDENCE_LEDGER_STORAGE_KEY);
      return defaults;
    }

    return parsed;
  } catch {
    return defaults;
  }
}

export function saveStudioPreviewEvidenceLedger(entries: StudioPreviewEvidenceEntry[]) {
  if (!localStorageAvailable()) {
    return false;
  }

  try {
    localStorage.setItem(
      STUDIO_PREVIEW_EVIDENCE_LEDGER_STORAGE_KEY,
      serializeLedger(
        entries
          .map((entry) => sanitizeEvidenceEntry(entry))
          .filter((entry): entry is StudioPreviewEvidenceEntry => Boolean(entry))
          .slice(0, STUDIO_PREVIEW_EVIDENCE_MAX_ENTRIES)
      )
    );
    return true;
  } catch {
    return false;
  }
}

export function appendStudioPreviewEvidence(entry: StudioPreviewEvidenceEntry | null) {
  if (!entry) {
    return null;
  }

  const sanitized = sanitizeEvidenceEntry(entry);
  if (!sanitized) {
    return null;
  }

  const current = loadStudioPreviewEvidenceLedger();
  saveStudioPreviewEvidenceLedger([sanitized, ...current.entries]);
  return sanitized;
}

export function resetStudioPreviewEvidenceLedger() {
  if (!localStorageAvailable()) {
    return false;
  }

  try {
    localStorage.removeItem(STUDIO_PREVIEW_EVIDENCE_LEDGER_STORAGE_KEY);
    return true;
  } catch {
    return false;
  }
}
